import { Spacing } from '@persona-id/design-tokens';
import { useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { GroupBase, OptionsOrGroups } from 'react-select';
import styled from 'styled-components';

import { filterDynamicOptions } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentConfigSetting/utils';
import {
  ComponentSettingMultiSelect,
  GroupedObjectOptionType,
  GroupedStringOptionType,
  ObjectOptionType,
  StringOptionType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import Markdown from 'dashboard/components/Markdown';
import ReactMultiSelect from 'dashboard/components/ReactMultiSelect';
import { OptionType } from 'dashboard/components/ReactSelect';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { InquiryTemplateVersionStep } from 'dashboard/models/inquiry-template-version';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Label, Paragraph } from 'design-system/components/Text';
import { FieldSchema } from 'lib/fields';
import { UiComponentConfig } from 'lib/interfaces/ui-components';
import { useFormSecurely } from 'lib/security/reactHookForm';

const StyledFormControlLabel = styled(FormControlLabel)`
  display: flex;
  align-items: center;
`;

const SelectedTypesContainer = styled.div`
  max-height: 200px;
  overflow-y: auto;
`;

const StickerContainer = styled.div`
  display: flex;
  gap: ${Spacing[2]} ${Spacing[1]};
  justify-content: flex-start;
  flex-direction: row;
  flex-wrap: wrap;
  padding-bottom: ${Spacing[2]};
`;

interface MultiSelectSettingProps {
  editable: boolean;
  componentSetting: ComponentSettingMultiSelect;
  componentConfig: UiComponentConfig['config'];
  steps: InquiryTemplateVersionStep[];
  updatePathPrefix: string | undefined;
  fieldSchema: FieldSchema[];
  handleUpdateComponentConfig: (componentConfigUpdates: Record<string, any>) => void;
}

type SelectValueType = string[] | object[];
type ReactMultiSelectOptions = OptionsOrGroups<OptionType<string>, GroupBase<OptionType<string>>> &
  OptionType<string>[];

// The following function converts our server passed options format:
// { label: string, value:string, group-label:string }[]
// to the format that react multiselect expects for group rendering
// {
//  label: string (group-label value),
//  options: {
//    label: string,
//    value: string
//  }[]
// }[]
const formatGroupOptions = (options: GroupedStringOptionType[]) => {
  const optionsKeyedByGroupLabel = options.reduce(
    (map: Record<string, StringOptionType[]>, option: GroupedStringOptionType) => {
      const { 'group-label': groupLabel, label, value } = option;
      if (map[groupLabel]) {
        map[groupLabel].push({ label, value });
      } else {
        map[groupLabel] = [{ label, value }];
      }
      return map;
    },
    {},
  );

  return Object.entries(optionsKeyedByGroupLabel).map(([groupLabel, options]) => ({
    label: groupLabel,
    value: null,
    options: options,
  })) as unknown as ReactMultiSelectOptions;
};

const isGroupedSelect = (
  value: ComponentSettingMultiSelect['options'],
): value is GroupedStringOptionType[] =>
  value != null && typeof value[0] == 'object' && 'group-label' in value[0];

const isObjectOptionType = (
  options: ComponentSettingMultiSelect['options'],
): options is ObjectOptionType[] | GroupedObjectOptionType[] => {
  const optionValue = options?.[0]?.value;
  return typeof optionValue === 'object' && optionValue != null;
};

const convertObjectToString = (value: object[]) => {
  return value.map((val) => JSON.stringify(val));
};

const convertStringToObjects = (value: string[]) => {
  return value.map((val) => JSON.parse(val));
};

const GroupedSelectedOptions = ({
  groupedSerializedOptions,
  selectedOptions,
}: {
  groupedSerializedOptions: ReactMultiSelectOptions;
  selectedOptions: string[];
}) => {
  return (
    <>
      {(
        groupedSerializedOptions as OptionsOrGroups<
          OptionType<string>,
          GroupBase<OptionType<string>>
        > &
          OptionType<string>[]
      ).map((groupedSelectedType) => {
        const groupedSelectedTypeOptions = groupedSelectedType?.options?.filter(({ value }) =>
          (selectedOptions as string[]).includes(value),
        );
        return (
          <>
            {groupedSelectedTypeOptions && groupedSelectedTypeOptions?.length > 0 && (
              <>
                <Label size='sm'>{groupedSelectedType.label}</Label>
                <StickerContainer>
                  {groupedSelectedTypeOptions.map((opt) => (
                    <Sticker key={`${groupedSelectedType.label}-${opt.label}`} variant='primary'>
                      {opt.label}
                    </Sticker>
                  ))}
                </StickerContainer>
              </>
            )}
          </>
        );
      })}
    </>
  );
};

const MultiSelectSetting = ({
  editable,
  componentSetting,
  componentConfig,
  steps,
  fieldSchema,
  updatePathPrefix,
  handleUpdateComponentConfig,
}: MultiSelectSettingProps): JSX.Element => {
  const {
    'client-key': clientKey,
    'helper-text': helperText,
    placeholder,
    options,
    'dynamic-option': dynamicOption,
    'select-all-enabled': selectAllEnabled,
    'search-enabled': searchEnabled,
  } = componentSetting;

  const serializeValue = isObjectOptionType(options);

  const serializedOptions = useMemo(() => {
    if (options) {
      return serializeValue
        ? options?.map((option) => ({ ...option, value: JSON.stringify(option.value) }))
        : options;
    } else {
      return filterDynamicOptions(dynamicOption, steps, fieldSchema);
    }
  }, [options, serializeValue, dynamicOption, steps, fieldSchema]);

  const serializedInitialValue = useMemo(() => {
    const initialValue =
      (componentConfig?.[
        clientKey as keyof UiComponentConfig['config']
      ] as unknown as SelectValueType) || [];

    const serializedOptionsValueSet = new Set(serializedOptions.map((option) => option.value));

    const serializedValues = serializeValue
      ? convertObjectToString(initialValue as object[])
      : (initialValue as string[]);

    return serializedValues.filter((value) => serializedOptionsValueSet.has(value));
  }, [clientKey, componentConfig, serializeValue, serializedOptions]);

  const { control, handleSubmit, reset, watch } = useFormSecurely({
    defaultValues: {
      [clientKey]: serializedInitialValue,
    },
  });

  const handleSubmitForm = handleSubmit((data) => {
    handleUpdateComponentConfig({
      [updatePathPrefix ? `${updatePathPrefix}.${clientKey}` : clientKey]: serializeValue
        ? convertStringToObjects(data[clientKey] as string[])
        : data[clientKey],
    });
  });

  useEffect(
    () =>
      reset({
        [clientKey]: serializedInitialValue,
      }),
    [serializedInitialValue, reset, clientKey],
  );

  const selectedOptions = watch(clientKey, []);
  const { groupedSerializedOptions, selectedValues } = useMemo(() => {
    const isGrouped = isGroupedSelect(serializedOptions);
    const groupedSerializedOptions = isGrouped
      ? formatGroupOptions(serializedOptions)
      : serializedOptions;

    const selectedValues = (
      <>
        {(selectedOptions as string[])?.length > 0 && (
          <>
            <Spacer height={1} />
            <Label size='md'>Selected</Label>
            <SelectedTypesContainer>
              {isGrouped ? (
                <GroupedSelectedOptions
                  groupedSerializedOptions={groupedSerializedOptions}
                  selectedOptions={selectedOptions as string[]}
                />
              ) : (
                <StickerContainer>
                  {serializedOptions
                    .filter(({ value }) => (selectedOptions as string[]).includes(value))
                    .map(({ label, value }) => (
                      <Sticker key={value} variant='primary' data-test={`country-codes--${value}`}>
                        {label}
                      </Sticker>
                    ))}
                </StickerContainer>
              )}
            </SelectedTypesContainer>
          </>
        )}
      </>
    );

    return { groupedSerializedOptions, selectedValues };
  }, [serializedOptions, selectedOptions]);

  return (
    <>
      <StyledFormControlLabel>
        {componentSetting.label}
        {helperText && (
          <HelpIcon>
            <Paragraph size='sm'>
              <Markdown>{helperText}</Markdown>
            </Paragraph>
          </HelpIcon>
        )}
      </StyledFormControlLabel>
      <Controller
        name={clientKey}
        control={control}
        render={({ field: { value, onChange } }) => (
          <ReactMultiSelect
            size='md'
            id={`form-section-multiselect-setting__${clientKey}`}
            placeholder={
              (value as string[])?.length > 0
                ? `${(value as string[]).length === serializedOptions.length ? 'All' : (value as string[]).length} selected`
                : placeholder
            }
            value={value as string[]}
            disabled={!editable}
            options={groupedSerializedOptions}
            selectAllEnabled={selectAllEnabled}
            searchEnabled={searchEnabled}
            onChange={(value) => {
              onChange(value);
              handleSubmitForm();
            }}
          />
        )}
      />
      {selectedValues}
    </>
  );
};

export default MultiSelectSetting;
